<template>
    <div class="company-pledged-shares">
        <PledgedShareChart :rows="rows" />
        <table class="mt-table">
            <thead>
                <tr>
                    <SortableColumnHeader label="Date" :sortable="false" />
                    <SortableColumnHeader class="multiline-header" label="Pledged / Owned" :sortable="false" />
                    <SortableColumnHeader class="multiline-header" label="Pledged / Total" :sortable="false" />
                    <SortableColumnHeader class="multiline-header" label="Change" :sortable="false" />
                </tr>
            </thead>
            <tbody>
                <tr v-for="row in rows" :key="row.effectiveDate" :class="{ highlighted: row.id === highlight?.id }">
                    <td data-label="Date">
                        <a :href="`https://www.sec.gov${row.href}`" target="_blank" v-if="row.href">{{
                            shortDate(row.effectiveDate)
                        }}</a>
                        <span v-else>
                            {{ shortDate(row.effectiveDate) }}
                        </span>
                    </td>
                    <td data-label="Pledged / Owned">{{ percent(row.pledgedOfOwned) }}</td>
                    <td data-label="Pledged / Total">{{ percent(row.pledgedOfOutstanding) }}</td>
                    <td data-label="Change">
                        {{ percent(row.change) }}
                        <i :data-category="row.changeCategory">{{ row.changeCategory }}</i>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script setup>
import { shortDate, formatNegativeParens } from '@/utils/format'
import PledgedShareChart from './PledgedShareChart.vue'
import SortableColumnHeader from '../shared/mt-table/SortableColumnHeader.vue'
import { computed } from 'vue'

const props = defineProps({
    pledgedShares: {
        type: Array,
        required: true,
    },
    highlight: {
        type: Object,
        required: false,
        default() {
            return null
        },
    },
})

function percent(value) {
    if (value === undefined || value === null) {
        return 'N/A'
    }

    return formatNegativeParens(value, { style: 'percent', maximumFractionDigits: 1 })
}

const rows = computed(() => {
    const pledgedShares = props.pledgedShares
        .slice(0)
        .sort((lhs, rhs) => rhs.effectiveDate.localeCompare(lhs.effectiveDate))
        .map((ps, i) => {
            const pledgedOfOwned =
                ps.totalPledgedShares == 0 ? 0 : ps.totalPledgedShares / ps.totalSharesBeneficiallyOwned
            const pledgedOfOutstanding = ps.totalSharesOutstanding
                ? ps.totalPledgedShares / ps.totalSharesOutstanding
                : null

            return { ...ps, pledgedOfOwned, pledgedOfOutstanding }
        })

    pledgedShares.forEach((ps, i, rows) => {
        const previous = rows[i + 1]
        ps.change = ps.pledgedOfOwned - (previous?.pledgedOfOwned ?? 0)
        ps.changeCategory = ps.change === 0 ? '-' : ps.change < 0 ? '▼' : '▲'
    })

    return pledgedShares
})
const highlightedRows = computed(() => rows.value.filter(r => props.highlight?.id === r.id))
</script>
<style>
.company-pledged-shares {
    .mt-table {
        td {
            text-align: right;
        }

        th {
            padding: 8px 16px;
            text-align: center;
        }

        td[data-label='Date'] {
            text-align: left;
            white-space: nowrap;
            width: 128px;
        }

        td[data-label='Change'] {
            white-space: nowrap;
        }

        [data-category] {
            display: inline-block;
            text-align: right;
            width: 12px;

            &[data-category='▲'] {
                color: var(--color-danger);
            }
            &[data-category='▼'] {
                color: green;
            }
        }
    }
}
</style>
